import { Box, Group, Stack, Text, Title, useMantineTheme } from '@mantine/core';
import { IconSparkles } from '@tabler/icons-react';
import { AiSummaryBasePanel } from 'app/Private/Clients/Detail/InfoTab/components/AiSummaryPanel/AiSummaryBasePanel';
import { i18nKeys, useTranslation } from 'locales/index';
import Markdown from 'react-markdown';
import { useParams } from 'react-router';
import { isDefined, isNonNullish } from 'remeda';
import { assert } from 'shared/utils';
import type { Debtor } from 'types/store/client-state';
import { AiSummaryOutdatedOverlay } from './AiSummaryOutdatedOverlay';
import { animatedGradientText, blackMarkdownText } from './AiSummaryPanel.styles';
import { useIsSummaryOutdated } from './hooks/use-is-summary-outdated';
import type { AiSummary } from './hooks/use-load-debtor-ai-summary';
import { FeedbackRatingIcon } from './SummaryFeedbackRating';

type AiSummaryContentProps = {
  aiSummary: NonNullable<AiSummary>;
  debtor: Debtor;
};

export const AiSummaryContent = ({ aiSummary, debtor }: AiSummaryContentProps) => {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const { debtorId } = useParams<{ debtorId: string }>();

  const { isOutdated, setIsOutdated } = useIsSummaryOutdated();

  // See loading conditions in AiSummaryPanel
  assert(isNonNullish(aiSummary.summary));
  if (!isDefined(debtor)) return null;

  const { summary } = aiSummary;

  return (
    <AiSummaryBasePanel
      topRight={
        !aiSummary.isOutdated && <FeedbackRatingIcon debtorId={debtorId} aiSummary={aiSummary} />
      }
      title={
        <Group gap={6}>
          <IconSparkles color={theme.colors.purple[6]} size={18} stroke={1.5} />
          <Title order={6}>
            <span className={animatedGradientText}>
              {t(i18nKeys.AI_SUMMARY.OVERVIEW_TITLE, { clientName: debtor.full_name })}
            </span>
          </Title>
        </Group>
      }
    >
      {isOutdated && (
        <AiSummaryOutdatedOverlay aiSummary={aiSummary} setIsOutdated={setIsOutdated} />
      )}
      <Stack gap="xs">
        <Text c="purple" fw={550}>
          {summary.overallAssessment}
        </Text>
        <Box className={blackMarkdownText}>
          <Markdown>{isOutdated ? summary.analysis.slice(0, 100) : summary.analysis}</Markdown>
        </Box>
        {summary.followUpStrategy && (
          <Box className={blackMarkdownText}>
            {!isOutdated && <Markdown>{summary.followUpStrategy}</Markdown>}
          </Box>
        )}
      </Stack>
    </AiSummaryBasePanel>
  );
};
